<template>
  <FormGroup :inputId="idCalc" :successes="successes">
    <slot name="label">
      <label :for="idCalc" v-if="label" class="tw-mb-2" :class="labelType" v-html="label" />
    </slot>
    <!-- TODO: wrap this in password strength meter (there's a vue package) -->
    <div v-if="unMask && passwordCheckbox" class="tw-float-right">
      <FormCheckbox
        name="maskCheck"
        @change="maskCheck"
        :value="false"
        label="Show"
        :tabindex="tabindex + 1"
        :testId="`${testId}-show-checkbox`"
      />
    </div>
    <div class="password-input">
      <input
        class="form-control !tw-px-4 !tw-py-2"
        :type="maskOff ? 'text' : 'password'"
        :class="[
          { 'is-invalid': passwordErrors, 'is-valid': successes, 'shift-error': shiftError },
          inputType,
        ]"
        :data-test="testId"
        :id="idCalc"
        :name="name"
        :placeholder="placeholder"
        :value="modelValue"
        :disabled="disabled"
        :tabindex="tabindex"
        @input="emitInput"
        @change="emitChange"
      />
      <template v-if="unMask && !passwordCheckbox">
        <IconButton
          @click="maskCheck"
          :icon="{
            default: { icon: `fa-regular ${maskOff ? 'fa-eye-slash' : 'fa-eye'}` },
            hover: { icon: `fa-solid ${maskOff ? 'fa-eye-slash' : 'fa-eye'}` },
          }"
          class="eye-icon"
          :tabindex="tabindex + 1"
        />
      </template>
    </div>
    <template #append-content>
      <div v-if="strengthMeter" class="password-strength-container">
        <div class="password-strength">
          <div class="password-strength-unit password-strength-0">
            <div
              class="password-strength-color"
              :class="{
                red: strengthColor === 'red',
                yellow: strengthColor === 'yellow',
                green: strengthColor === 'green',
              }"
            />
          </div>
          <div class="password-strength-unit password-strength-1">
            <div
              class="password-strength-color"
              :class="{
                red: strengthColor === 'red' && passwordStrength > 1,
                yellow: strengthColor === 'yellow',
                green: strengthColor === 'green',
              }"
            />
          </div>
          <div class="password-strength-unit password-strength-2">
            <div
              class="password-strength-color"
              :class="{
                green: strengthColor === 'green',
              }"
            />
          </div>
          <div class="password-strength-unit password-strength-3">
            <div
              class="password-strength-color"
              :class="{
                green: strengthColor === 'green' && passwordStrength > 3,
              }"
            />
          </div>
        </div>
        <div class="password-help-tip">
          <div v-if="passwordStrength < 3" class="tw-flex tw-items-center">
            <div
              v-for="(error, e) in passwordErrors"
              :key="e"
              class="nx-text-body4 tw-text-danger-text-medium"
            >
              {{ error }}
            </div>
          </div>
          <div class="tw-flex-grow tw-flex tw-justify-end tw-items-center">
            <span class="password-tip-text" :class="[strengthColor]">{{ passwordTip }}</span>
            <Popover header="Password Tips" :testId="`${testId}-password-tips`">
              <template #message>
                <div class="use-tips">
                  <h6>Use:</h6>
                  <ul>
                    <li>Long passwords.</li>
                    <li>LastPass or Nexcess's password generator.</li>
                    <li>Random words, punctuation, numbers and symbols.</li>
                  </ul>
                </div>
                <div class="dont-use-tips">
                  <h6>Do not use:</h6>
                  <ul>
                    <li>A short password.</li>
                    <li>A word repeated several times.</li>
                    <li>A password you used in other sites.</li>
                    <li>
                      Passwords that can easily be identified with you or your social media
                      accounts.
                    </li>
                    <li>Common keyboard patterns like: \"1234\" or \"abcdefg\".</li>
                  </ul>
                </div>
              </template>
              <font-awesome-icon icon="fa-regular fa-circle-question" class="tw-text-[20px]" />
            </Popover>
          </div>
        </div>
      </div>
    </template>
  </FormGroup>
</template>

<script>
import { computed, ref, watch } from "vue";
import FormCheckbox from "@/components/display/form/FormCheckbox";
import FormGroup from "@/components/display/form/FormGroup";
import FormMixin from "@/components/display/form/FormMixin";
import IconButton from "@/components/display/IconButton.vue";
import { passwordStrength as passwordStrengthRule } from "@/utils/password";
import Popover from "@/components/display/Popover";

export default {
  name: "FormPassword",
  components: {
    FormCheckbox,
    FormGroup,
    IconButton,
    Popover,
  },
  mixins: [FormMixin],
  emits: ["change", "input", "update:modelValue", "update:strength"],
  props: {
    modelValue: [String, Number],
    type: {
      type: String,
      default: "text",
    },
    strengthMeter: {
      type: Boolean,
      default: false,
    },
    unMask: {
      type: Boolean,
      default: true,
    },
    passwordCheckbox: {
      type: Boolean,
      default: true,
    },
    strength: Number,
    variant: String,
    testId: String,
  },
  setup(props, { emit }) {
    const maskOff = ref(false);
    const maskCheck = () => {
      maskOff.value = !maskOff.value;
    };
    const passwordErrors = computed(() => {
      if (props.errorsDisplay === "always" || (props.errorsDisplay === "changed" && props.dirty)) {
        if (!props.errors && passwordStrength.value < 3) {
          return ["Please provide a stronger password."];
        }

        return props.errors;
      }

      return false;
    });
    const passwordStrength = computed(() => passwordStrengthRule(props.modelValue));
    const passwordTip = computed(() => {
      const strength = passwordStrength.value;

      if (!props.modelValue) {
        return "Enter Password";
      } else if (strength === 0) {
        return "Very Weak";
      } else if (strength === 1) {
        return "Very Weak";
      } else if (strength === 2) {
        return "Weak";
      } else if (strength === 3) {
        return "Strong";
      } else {
        return "Very Strong";
      }
    });

    const strengthColor = computed(() => {
      const strength = passwordStrength.value;

      if (strength === 0) {
        return "grey";
      } else if (strength === 1) {
        return "red";
      } else if (strength === 2) {
        return "yellow";
      } else if (strength === 3) {
        return "green";
      } else {
        return "green";
      }
    });

    const emitChange = (e) => emit("change", e.target.value);
    const emitInput = (e) => {
      emit("input", e.target.value);
      emit("update:modelValue", e.target.value);
      emit("update:strength", passwordStrength.value);
    };

    const labelTypeList = {
      light: "",
    };
    const labelType = computed(() =>
      !labelTypeList[props.variant] ? labelTypeList.light : labelTypeList[props.variant]
    );
    const inputTypeList = {
      light: "",
    };
    const inputType = computed(() =>
      !inputTypeList[props.variant] ? inputTypeList.light : inputTypeList[props.variant]
    );
    const shiftError = computed(() => !props.passwordCheckbox);

    watch(
      () => props.modelValue,
      () => {
        emit("update:strength", passwordStrength.value);
      }
    );

    return {
      emitChange,
      emitInput,
      passwordErrors,
      passwordStrength,
      passwordTip,
      strengthColor,
      maskOff,
      maskCheck,
      labelType,
      inputType,
      shiftError,
    };
  },
};
</script>

<style lang="scss" scoped>
.password-strength-container {
  .password-help-tip {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    display: flex;

    .password-tip-text {
      transition: color 1s ease;
      font-size: 0.75rem;
      margin-right: 0.5rem;
    }
  }
  .password-strength {
    display: flex;
    flex-direction: row;
    height: 3px;
    margin-left: -5px;
    margin-right: -5px;
    margin-top: 5px;

    .password-strength-unit {
      flex: 0 0 25%;
      max-width: 25%;
      height: 3px;
      padding-right: 5px;
      padding-left: 5px;
      width: 100%;

      .password-strength-color {
        transition: background-color 1s ease;
        width: 100%;
        height: 100%;
      }
    }
  }
}

.password-input {
  input {
    display: inline;
    width: 100%;
  }

  .eye-icon {
    display: inline;
    margin-left: -30px;
    cursor: pointer;
  }
}

ul {
  list-style: initial;
  padding-left: 14px;
}

.form-control.is-invalid.shift-error {
  background-position: right calc(2.375em + 0.1875rem) center !important;
}
</style>
